import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { STATUS_LABEL, type Issue, type Status } from '../lib/types';

export function StatusPill({ status }: { status: Status }) {
  return <span className={`pill ${status}`}>{STATUS_LABEL[status]}</span>;
}

export function Spinner() {
  return <span className="spinner" aria-label="working" />;
}

export function Field(props: {
  label: string;
  value: string | number | null;
  onChange: (value: string) => void;
  hint?: string;
  type?: 'text' | 'number' | 'password';
  placeholder?: string;
  pending?: boolean;
}) {
  const { label, value, onChange, hint, type = 'text', placeholder, pending } = props;
  return (
    <div className={`field ${pending ? 'pending' : ''}`}>
      <label>{label}</label>
      <input
        type={type}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

export function TextArea(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  rows?: number;
}) {
  return (
    <div className="field">
      <label>{props.label}</label>
      <textarea
        value={props.value}
        rows={props.rows}
        onChange={(e) => props.onChange(e.target.value)}
      />
      {props.hint && <div className="hint">{props.hint}</div>}
    </div>
  );
}

/** Edit a list of plain strings (includes, route cities, notes, ...). */
export function ListEditor(props: {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  addLabel?: string;
}) {
  const { label, items, onChange, placeholder, addLabel = 'Add item' } = props;
  const update = (index: number, value: string) =>
    onChange(items.map((item, i) => (i === index ? value : item)));

  return (
    <div className="field list-editor">
      <label>{label}</label>
      {items.map((item, index) => (
        <div className="list-row" key={index}>
          <input value={item} placeholder={placeholder} onChange={(e) => update(index, e.target.value)} />
          <button
            className="btn small ghost"
            title="Move up"
            disabled={index === 0}
            onClick={() => {
              const next = [...items];
              [next[index - 1], next[index]] = [next[index], next[index - 1]];
              onChange(next);
            }}
          >↑</button>
          <button
            className="btn small danger"
            title="Remove"
            onClick={() => onChange(items.filter((_, i) => i !== index))}
          >×</button>
        </div>
      ))}
      <button className="btn small" onClick={() => onChange([...items, ''])}>+ {addLabel}</button>
    </div>
  );
}

export function IssueList({ issues, limit }: { issues: Issue[]; limit?: number }) {
  if (!issues.length) return <div className="sub">Nothing outstanding — this booklet is ready to publish.</div>;
  const shown = limit ? issues.slice(0, limit) : issues;
  return (
    <div className="issues">
      {shown.map((issue, i) => (
        <div className={`issue ${issue.level}`} key={`${issue.field}-${i}`}>
          <div>
            <div>{issue.message}</div>
            <div className="path">{issue.field}</div>
          </div>
        </div>
      ))}
      {limit && issues.length > limit && (
        <div className="sub">+ {issues.length - limit} more</div>
      )}
    </div>
  );
}

export function Dropzone(props: {
  title: string;
  hint: string;
  accept: string;
  busy?: boolean;
  onFile: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  return (
    <div
      className={`dropzone ${over ? 'over' : ''}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) props.onFile(file);
      }}
    >
      <div className="big">{props.busy ? 'Reading…' : props.title}</div>
      <div className="small">{props.hint}</div>
      <input
        ref={inputRef}
        type="file"
        accept={props.accept}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) props.onFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------- toasts
type Toast = { id: number; message: string; error?: boolean };
type ToastApi = { push: (message: string, error?: boolean) => void };

const ToastContext = createContext<ToastApi>({ push: () => {} });
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, error = false) => {
    const id = Date.now() + Math.random();
    setToasts((all) => [...all, { id, message, error }]);
    setTimeout(() => setToasts((all) => all.filter((t) => t.id !== id)), error ? 7000 : 3500);
  }, []);

  const api = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toasts.map((toast, i) => (
        <div
          key={toast.id}
          className={`toast ${toast.error ? 'error' : ''}`}
          style={{ bottom: 20 + i * 64 }}
        >
          {toast.message}
        </div>
      ))}
    </ToastContext.Provider>
  );
}
